import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InboxEntry } from '@this+that/models';

import {
  BoardSelection,
  clampRowIndex,
  clampSelection,
  computeSelectedFlatIndex,
  EMPTY_SELECTION,
  findFirstNonEmptyColumn,
  findNextNonEmptyColumn,
  handleArrowKeyDown,
} from './boardNavigationUtils';
import { isVisibleDialog } from './shouldIgnoreShortcut';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { useDoBoxKeyboardFocusStore } from '../../store/DoBox/useDoBoxKeyboardFocusStore';

interface UseDoBoxBoardNavigationProps {
  columns: InboxEntry[][];
  enabled?: boolean;
  onOpenModal?: (entryId: string) => void;
}

export function useDoBoxBoardNavigation({
  columns,
  enabled = true,
  onOpenModal,
}: UseDoBoxBoardNavigationProps) {
  const [selection, setSelection] = useState<BoardSelection>(EMPTY_SELECTION);
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts({
    disableFocusSteal: true,
  });
  const setFocusedEntryId = useDoBoxKeyboardFocusStore(
    (s) => s.setFocusedEntryId,
  );

  const columnsRef = useRef(columns);
  const selectionRef = useRef(selection);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  useEffect(() => {
    setSelection((prev) => clampSelection(prev, columns));
  }, [columns]);

  const selectedEntryId =
    columns[selection.columnIndex]?.[selection.rowIndex]?.id ?? null;

  const selectedFlatIndex = useMemo(
    () => computeSelectedFlatIndex(columns, selection),
    [columns, selection],
  );

  useEffect(() => {
    if (selectedEntryId) {
      const element = document.querySelector(
        `[data-dobox-entry-id="${selectedEntryId}"]`,
      );
      element?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedEntryId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    document.addEventListener('keydown', handleArrowKeyDown);
    return () => {
      document.removeEventListener('keydown', handleArrowKeyDown);
    };
  }, [enabled]);

  useEffect(() => {
    setFocusedEntryId(enabled ? selectedEntryId : null);
  }, [enabled, selectedEntryId, setFocusedEntryId]);

  useEffect(() => {
    return () => {
      setFocusedEntryId(null);
    };
  }, [setFocusedEntryId]);

  const selectFirstAvailableColumn = useCallback((): BoardSelection | null => {
    const cols = columnsRef.current;
    const first = findFirstNonEmptyColumn(cols);
    if (first === -1) {
      return null;
    }
    return { columnIndex: first, rowIndex: 0 };
  }, []);

  const handleNavigateColumn = useCallback(
    (direction: 1 | -1) => {
      const cols = columnsRef.current;
      if (cols.length === 0) {
        return;
      }
      const current = selectionRef.current;
      if (current.columnIndex === -1) {
        const next = selectFirstAvailableColumn();
        if (next) {
          setSelection(next);
        }
        return;
      }
      const nextColumnIndex = findNextNonEmptyColumn(
        cols,
        current.columnIndex,
        direction,
      );
      if (nextColumnIndex === -1) {
        return;
      }
      const rowIndex = clampRowIndex(
        current.rowIndex,
        cols[nextColumnIndex]?.length ?? 0,
      );
      setSelection({ columnIndex: nextColumnIndex, rowIndex });
    },
    [selectFirstAvailableColumn],
  );

  const handleNavigateRow = useCallback(
    (direction: 1 | -1) => {
      const cols = columnsRef.current;
      const current = selectionRef.current;
      if (current.columnIndex === -1) {
        const next = selectFirstAvailableColumn();
        if (next) {
          setSelection(next);
        }
        return;
      }
      const columnLength = cols[current.columnIndex]?.length ?? 0;
      if (columnLength === 0) {
        return;
      }
      const nextRowIndex =
        current.rowIndex === -1
          ? direction === 1
            ? 0
            : columnLength - 1
          : Math.max(
              0,
              Math.min(current.rowIndex + direction, columnLength - 1),
            );
      setSelection({
        columnIndex: current.columnIndex,
        rowIndex: nextRowIndex,
      });
    },
    [selectFirstAvailableColumn],
  );

  const handleNavigateRight = useCallback(
    () => handleNavigateColumn(1),
    [handleNavigateColumn],
  );
  const handleNavigateLeft = useCallback(
    () => handleNavigateColumn(-1),
    [handleNavigateColumn],
  );
  const handleNavigateDown = useCallback(
    () => handleNavigateRow(1),
    [handleNavigateRow],
  );
  const handleNavigateUp = useCallback(
    () => handleNavigateRow(-1),
    [handleNavigateRow],
  );

  const handleHome = useCallback(() => {
    const current = selectionRef.current;
    if (current.columnIndex === -1) {
      return;
    }
    setSelection({ columnIndex: current.columnIndex, rowIndex: 0 });
  }, []);

  const handleEnd = useCallback(() => {
    const cols = columnsRef.current;
    const current = selectionRef.current;
    if (current.columnIndex === -1) {
      return;
    }
    const columnLength = cols[current.columnIndex]?.length ?? 0;
    setSelection({
      columnIndex: current.columnIndex,
      rowIndex: Math.max(0, columnLength - 1),
    });
  }, []);

  const handleEnter = useCallback(() => {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    const hasVisiblePopover = Array.from(dialogs).some(isVisibleDialog);
    if (hasVisiblePopover) {
      return;
    }
    const cols = columnsRef.current;
    const current = selectionRef.current;
    const entryId = cols[current.columnIndex]?.[current.rowIndex]?.id;
    if (entryId && onOpenModal) {
      onOpenModal(entryId);
    }
  }, [onOpenModal]);

  const resetSelection = useCallback(() => {
    setSelection(EMPTY_SELECTION);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    registerShortcut({
      key: ShortcutKey.ARROW_RIGHT,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateRight,
      description: 'Navigate to next DoBox board column',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_LEFT,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateLeft,
      description: 'Navigate to previous DoBox board column',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_DOWN,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateDown,
      description: 'Navigate to next DoBox board item',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_UP,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateUp,
      description: 'Navigate to previous DoBox board item',
    });

    registerShortcut({
      key: ShortcutKey.NEXT,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateDown,
      description: 'Navigate to next DoBox board item',
    });

    registerShortcut({
      key: ShortcutKey.PREVIOUS,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateUp,
      description: 'Navigate to previous DoBox board item',
    });

    registerShortcut({
      key: ShortcutKey.HOME,
      contexts: [ShortcutContext.DOBOX],
      handler: handleHome,
      description: 'Navigate to first DoBox board item in column',
    });

    registerShortcut({
      key: ShortcutKey.END,
      contexts: [ShortcutContext.DOBOX],
      handler: handleEnd,
      description: 'Navigate to last DoBox board item in column',
    });

    registerShortcut({
      key: ShortcutKey.ENTER,
      contexts: [ShortcutContext.DOBOX],
      handler: handleEnter,
      description: 'Open DoBox board item modal',
    });

    const ctx = [ShortcutContext.DOBOX];
    return () => {
      unregisterShortcut(ShortcutKey.ARROW_RIGHT, ctx);
      unregisterShortcut(ShortcutKey.ARROW_LEFT, ctx);
      unregisterShortcut(ShortcutKey.ARROW_DOWN, ctx);
      unregisterShortcut(ShortcutKey.ARROW_UP, ctx);
      unregisterShortcut(ShortcutKey.NEXT, ctx);
      unregisterShortcut(ShortcutKey.PREVIOUS, ctx);
      unregisterShortcut(ShortcutKey.HOME, ctx);
      unregisterShortcut(ShortcutKey.END, ctx);
      unregisterShortcut(ShortcutKey.ENTER, ctx);
    };
  }, [
    enabled,
    registerShortcut,
    unregisterShortcut,
    handleNavigateRight,
    handleNavigateLeft,
    handleNavigateDown,
    handleNavigateUp,
    handleHome,
    handleEnd,
    handleEnter,
  ]);

  return {
    selectedEntryId,
    selectedFlatIndex,
    resetSelection,
  };
}
